import { Head, Link, router } from "@inertiajs/react"
import { Plus, ArrowRight, Upload, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"

type RedirectRow = {
  id: number
  source_path: string
  destination: string
  redirect_type: string
  click_count: number
  last_clicked_at: string | null
}

type SortColumn = "source_path" | "click_count" | "last_clicked_at"

function SortHeader({
  column,
  label,
  currentSort,
  currentDir,
}: {
  column: SortColumn
  label: string
  currentSort: string
  currentDir: string
}) {
  const isActive = currentSort === column
  const nextDir = isActive && currentDir === "desc" ? "asc" : "desc"
  const Icon = isActive ? (currentDir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown

  return (
    <button
      onClick={() => router.get("/admin/redirects", { sort: column, dir: nextDir }, { preserveState: true })}
      className="inline-flex items-center gap-1 font-medium text-ink-muted hover:text-ink-display"
    >
      {label}
      <Icon className={`h-3.5 w-3.5 ${isActive ? "text-accent-display" : ""}`} />
    </button>
  )
}

function formatDate(iso: string | null) {
  if (!iso) return "—"
  const d = new Date(iso)
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
}

export default function AdminRedirectsIndex({
  redirects,
  sort,
  dir,
}: {
  redirects: RedirectRow[]
  sort: string
  dir: string
}) {
  const totalClicks = redirects.reduce((sum, r) => sum + r.click_count, 0)

  return (
    <>
      <Head title="Redirects">
        <meta name="description" content="Manage redirects and track clicks." />
        <meta property="og:title" content="Redirects" />
        <meta property="og:description" content="Manage redirects and track clicks." />
      </Head>
      <AdminShell>
        <div className="flex flex-col gap-4 border-b border-hairline pb-6 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1>Redirects</h1>
            <p className="mt-1">
              {redirects.length} {redirects.length === 1 ? "redirect" : "redirects"} &middot; {totalClicks.toLocaleString()} total clicks
            </p>
          </div>
          <div className="flex gap-2">
            <Button asChild variant="secondary">
              <Link href="/admin/redirect_imports/new">
                <Upload className="h-4 w-4" />
                Import CSV
              </Link>
            </Button>
            <Button asChild>
              <Link href="/admin/redirects/new">
                <Plus className="h-4 w-4" />
                New redirect
              </Link>
            </Button>
          </div>
        </div>

        {redirects.length === 0 ? (
          <p className="mt-10 text-ink-muted">
            No redirects yet. Add one, or import your Pretty Links CSV.
          </p>
        ) : (
          <div className="mt-6 overflow-x-auto rounded-md border border-hairline">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-hairline bg-surface text-left">
                  <th className="px-4 py-3">
                    <SortHeader column="source_path" label="Source path" currentSort={sort} currentDir={dir} />
                  </th>
                  <th className="px-4 py-3 font-medium text-ink-muted">Destination</th>
                  <th className="px-4 py-3 font-medium text-ink-muted">Type</th>
                  <th className="px-4 py-3 text-right">
                    <SortHeader column="click_count" label="Clicks" currentSort={sort} currentDir={dir} />
                  </th>
                  <th className="px-4 py-3">
                    <SortHeader column="last_clicked_at" label="Last click" currentSort={sort} currentDir={dir} />
                  </th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-hairline">
                {redirects.map((r) => (
                  <tr key={r.id} className="hover:bg-surface">
                    <td className="px-4 py-3">
                      <code className="text-ink-display">{r.source_path}</code>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-2 text-ink-muted">
                        <ArrowRight className="h-3.5 w-3.5 shrink-0" />
                        <span className="break-all">{r.destination}</span>
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Badge tone={r.redirect_type === "301" ? "accent" : "neutral"}>
                        {r.redirect_type}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {r.click_count.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-ink-muted">
                      {formatDate(r.last_clicked_at)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <Link
                        href={`/admin/redirects/${r.id}/edit`}
                        className="text-sm text-accent-display no-underline hover:underline"
                      >
                        Edit
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AdminShell>
    </>
  )
}
